import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  WifiTethering as ConnectionIcon,
} from '@mui/icons-material';
import ApiService from '../services/api';

interface NotificationPreference {
  id: string;
  user_id: string;
  notification_service_id: string;
  user_key: string | null;
  device_name: string | null;
  is_enabled: boolean;
  priority: number;
  preferences: Record<string, any>;
  service_name: string;
  service_type: string;
}

interface NotificationService {
  id: string;
  service_name: string;
  service_type: string;
  is_enabled: boolean;
  is_connected: boolean;
}

const MyNotificationsPage: React.FC = () => {
  const [preferences, setPreferences] = useState<NotificationPreference[]>([]);
  const [availableServices, setAvailableServices] = useState<NotificationService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);
  const [validating, setValidating] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    notification_service_id: '',
    user_key: '',
    device_name: '',
    is_enabled: true,
    priority: 0
  });

  const loadPreferences = async (preserveError: boolean = false) => {
    try {
      setLoading(true);
      if (!preserveError) {
        setError(null);
      }
      const [prefsData, servicesData] = await Promise.all([
        ApiService.getMyNotificationPreferences(),
        ApiService.getNotificationServices()
      ]);
      setPreferences(prefsData);
      // Only show enabled services
      setAvailableServices(servicesData.filter((s: NotificationService) => s.is_enabled));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPreferences();
  }, []);

  const handleToggle = async (preferenceId: string, currentEnabled: boolean) => {
    try {
      setUpdating(preferenceId);
      setError(null);
      
      await ApiService.updateNotificationPreference(preferenceId, { is_enabled: !currentEnabled });
      
      setPreferences(prev =>
        prev.map(pref =>
          pref.id === preferenceId
            ? { ...pref, is_enabled: !currentEnabled }
            : pref
        )
      );
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to toggle preference');
    } finally {
      setUpdating(null);
    }
  };

  const handleValidate = async (preferenceId: string) => {
    try {
      setValidating(preferenceId);
      setError(null);
      
      console.log('Starting validation for preference:', preferenceId);
      
      const result = await ApiService.validateNotificationPreference(preferenceId);
      
      console.log('Validation result:', result);
      
      // Always show feedback - success or error
      if (result && result.valid === true) {
        // Show success message
        let successMsg = result.message || 'User key validated successfully';
        
        // Add device information if available
        if (result.devices && result.devices.length > 0) {
          successMsg += `. Available devices: ${result.devices.join(', ')}`;
        }
        
        // Show warning if device not found or other warning
        if (result.warning) {
          // User key is valid, but device name issue
          setError(`⚠️ User key is valid. ${result.warning}`);
          if (result.devices && result.devices.length > 0) {
            setTimeout(() => {
              setError(`✅ User key validated. Available devices: ${result.devices.join(', ')}`);
            }, 4000);
          }
        } else {
          setError(`✅ ${successMsg}`); // Show success message
        }
      } else if (result && result.valid === false) {
        // Show error message
        const errorMsg = result.error || result.message || 'Validation failed';
        setError(`❌ ${errorMsg}`);
      } else {
        // Fallback for unexpected response format
        console.error('Unexpected validation result:', result);
        setError(`⚠️ Validation completed with unknown result. Check console for details.`);
      }
      
      // Reload preferences to get any updated status (but preserve error message)
      await loadPreferences(true);
      // Restore the error message after reload completes (loadPreferences clears it)
      setTimeout(() => {
        if (result && result.valid === true) {
          let successMsg = result.message || 'User key validated successfully';
          if (result.devices && result.devices.length > 0) {
            successMsg += `. Available devices: ${result.devices.join(', ')}`;
          }
          if (result.warning) {
            setError(`⚠️ User key is valid. ${result.warning}`);
          } else {
            setError(`✅ ${successMsg}`);
          }
        } else if (result && result.valid === false) {
          const errorMsg = result.error || result.message || 'Validation failed';
          setError(`❌ ${errorMsg}`);
        }
      }, 100);
    } catch (err: any) {
      console.error('Validation error:', err);
      const errorMsg = err.response?.data?.detail || err.response?.data?.error || err.message || 'Failed to validate';
      setError(`❌ ${errorMsg}`);
    } finally {
      setValidating(null);
    }
  };

  const handleOpenDialog = (pref?: NotificationPreference) => {
    if (pref) {
      setEditingId(pref.id);
      setFormData({
        notification_service_id: pref.notification_service_id,
        user_key: pref.user_key || '',
        device_name: pref.device_name || '',
        is_enabled: pref.is_enabled,
        priority: pref.priority
      });
    } else {
      setEditingId(null);
      setFormData({
        notification_service_id: '',
        user_key: '',
        device_name: '',
        is_enabled: true,
        priority: 0
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    try {
      setError(null);
      
      // Ensure preferences field is included (empty object if not set)
      const payload = {
        ...formData,
        preferences: {} // Always include preferences field as empty object
      };
      
      if (editingId) {
        await ApiService.updateNotificationPreference(editingId, payload);
      } else {
        await ApiService.createNotificationPreference(payload);
      }
      
      handleCloseDialog();
      await loadPreferences();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save preference');
    }
  };

  const handleDelete = async (preferenceId: string) => {
    if (!window.confirm('Are you sure you want to delete this notification preference?')) {
      return;
    }
    
    try {
      setError(null);
      await ApiService.deleteNotificationPreference(preferenceId);
      await loadPreferences();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete preference');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center">
          <NotificationsIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            My Notifications
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Notification
        </Button>
      </Box>

      {error && (
        <Alert 
          severity={error.startsWith('✅') ? "success" : (error.startsWith('❌') ? "error" : "warning")} 
          sx={{ mb: 3 }} 
          onClose={() => setError(null)}
        >
          {error.replace(/^[✅❌]\s*/, '')}
        </Alert>
      )}

      {preferences.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Notification Preferences
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Configure notification services to receive outage alerts
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Your First Notification
          </Button>
        </Paper>
      ) : (
        <Paper sx={{ p: 3 }}>
          <List>
            {preferences.map((pref, index) => (
              <React.Fragment key={pref.id}>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                          {pref.service_name}
                        </Typography>
                        {pref.is_enabled && (
                          <Chip label="ACTIVE" color="success" size="small" />
                        )}
                        {pref.is_enabled && pref.user_key && (
                          <Chip icon={<CheckIcon />} label="CONFIGURED" color="info" size="small" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Type: {pref.service_type}
                        </Typography>
                        {pref.user_key && (
                          <Typography variant="caption" color="text.secondary">
                            User Key: {pref.user_key.substring(0, 8)}...
                          </Typography>
                        )}
                        {pref.device_name && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Device: {pref.device_name}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" display="block">
                          Priority: {pref.priority}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box display="flex" alignItems="center" gap={1}>
                      {pref.user_key && (
                        <Tooltip title="Validate Configuration">
                          <IconButton
                            onClick={() => handleValidate(pref.id)}
                            disabled={validating === pref.id}
                            size="small"
                          >
                            {validating === pref.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <ConnectionIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton
                        onClick={() => handleOpenDialog(pref)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(pref.id)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                      <Switch
                        checked={pref.is_enabled}
                        onChange={() => handleToggle(pref.id, pref.is_enabled)}
                        disabled={updating === pref.id}
                      />
                      {updating === pref.id && (
                        <CircularProgress size={20} />
                      )}
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < preferences.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? 'Edit' : 'Add'} Notification Preference
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <FormControl fullWidth disabled={!!editingId}>
              <InputLabel>Notification Service</InputLabel>
              <Select
                value={formData.notification_service_id}
                onChange={(e) => setFormData({ ...formData, notification_service_id: e.target.value })}
                label="Notification Service"
              >
                {availableServices.map((service) => (
                  <MenuItem key={service.id} value={service.id}>
                    {service.service_name} ({service.service_type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="User Key"
              value={formData.user_key}
              onChange={(e) => setFormData({ ...formData, user_key: e.target.value })}
              fullWidth
              helperText="Your Pushover user key, phone number, or email"
              required
            />
            <TextField
              label="Device Name (Optional)"
              value={formData.device_name}
              onChange={(e) => setFormData({ ...formData, device_name: e.target.value })}
              fullWidth
              helperText="Specific device for Pushover"
            />
            <TextField
              label="Priority"
              type="number"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
              fullWidth
              helperText="Notification priority (0-9)"
              inputProps={{ min: 0, max: 9 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formData.notification_service_id || !formData.user_key}>
            {editingId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyNotificationsPage;

